'use strict';
//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** Niche
//***********************************************************************************
//***********************************************************************************

import { cn_element } from './cn_element';
import { cn_add, cn_box, cn_dot, cn_middle, cn_mul, cn_normal, cn_normalize, cn_sub, cnx_clone, cnx_mul } from '../utils/cn_utilities';
import { cn_niche_type } from './cn_niche_type';
import { cn_storey } from './cn_storey';
import { cn_element_visitor } from '../utils/visitors/cn_element_visitor';
import { cn_scene } from './cn_scene';
import { logger } from '../utils/cn_logger';
import { cn_vertex } from './cn_vertex';
import { cn_transaction_manager } from '../utils/cn_transaction_manager';
import { cn_camera } from '../svg/cn_camera';
import { fh_polygon, fh_solid } from '@acenv/fh-3d-viewer';
import { cn_facing_group } from './cn_facing';
import { cn_constant, CN_FACING_SUPPORTS } from './cn_model_constants';

export class cn_niche extends cn_element {
    //***********************************************************************************
    /**
     * Constructor
     * @param {cn_niche_type} element_type
     * @param {cn_scene} scene
     */
    constructor(element_type, scene) {
        super(scene);
        this.class_name = 'cn_niche';

        //*** Model data
        this.wall = null;
        this.wall_position = 0;
        this.wall_side = 0;
        this.width = 0.5;
        this.height = 0.5;
        this.shape_height = 0.2;
        this.depth = 0.1;
        this.z = 1;
        this.element_type = element_type;
        this.facings = [{}, {}];
        this.substrates = [{}, {}];
        this.wall_lock_vertex = null;
        this.lock_position = 0;

        this.valid = true;
        this.wall_interface = null;
    }

    //***********************************************************************************
    //**** serialize
    //***********************************************************************************
    serialize() {
        var json = this.serialize_element();
        json.wall_position = this.wall_position;
        json.wall_side = this.wall_side;
        json.width = this.width;
        json.height = this.height;
        json.depth = this.depth;
        json.z = this.z;
        json.element_type = this.element_type.ID;
        json.facings = [{}, {}];
        this.get_facing_groups().forEach(fg => {
            fg.roles.forEach(role => {
                let facing = this.facings[fg.side][role.code];
                if (typeof (facing) != 'undefined') 
                    json.facings[fg.side][role.code] = (facing) ? facing.ID : null;
                let substrate = this.substrates[fg.side][role.code];
                if (typeof (substrate) != 'undefined') 
                    json.substrates[fg.side][role.code] = (substrate) ? substrate.ID : null;
            });
        });
        // @ts-ignore
        json.wall_lock_vertex = (this.wall_lock_vertex) ? this.wall_lock_vertex.s_index : -1;
        return json;
    }

    static unserialize(json, scene) {
        if (typeof (json.ID) != 'string') return false;
        if (typeof (json.element_type) != 'string') return false;

        var et = null;
        if (typeof (json.element_type) == 'string')
            et = scene.building.get_element_type(json.element_type);
        if (et == null) {
            logger.log('use default beam type');
            et = scene.building.get_niche_types()[0];
        }

        var niche = new cn_niche(et, scene);
        niche.unserialize_element(json);

        if (typeof (json.wall_position) == 'number')
            niche.wall_position = json.wall_position;

        if (typeof (json.wall_side) == 'number')
            niche.wall_side = json.wall_side;

        if (typeof (json.height) == 'number')
            niche.height = json.height;

        if (typeof (json.width) == 'number')
            niche.width = json.width;

        if (typeof (json.z) == 'number')
            niche.z = json.z;

        if (typeof (json.depth) == 'number')
            niche.depth = json.depth;

        //*** Obsolete */
        if (json.facing) {
            niche.facings[0]["back"] = scene.building.facing_types.find(f => f.ID === json.facing.ID);
        }
        else if (json.facings) {
            json.facings.forEach((fg, side) => {
                for (var key in fg)
                    niche.facings[side][key] = scene.building.facing_types.find(f => f.ID === fg[key]) || null;
            });
        }

        if (json.substrates) {
            json.substrates.forEach((fg, side) => {
                for (var key in fg)
                    niche.substrates[side][key] = scene.building.get_mmm_compositions().find(f => f.ID === fg[key]) || null;
            });
        }

        if (typeof (json.wall_lock_vertex) == 'number') {
            niche.wall_lock_vertex = scene.vertices[json.wall_lock_vertex] || null;
            niche.lock_position = NaN;
        }

        return niche;
    }

    /**
     *
     * @returns {cn_niche}
     */
    clone() {
        let c = new cn_niche(this.element_type, this.parent);
        c.wall = this.wall;
        c.wall_position = this.wall_position;
        c.wall_side = this.wall_side;
        c.width = this.width;
        c.height = this.height;
        c.depth = this.depth;
        c.z = this.z;
        c.element_type = this.element_type;
        c.facings = cn_facing_group.clone(this.facings);
        c.substrates = cn_facing_group.clone(this.substrates);
        c.wall_lock_vertex = this.wall_lock_vertex;
        return c;
    }

    /**
     * Retruns a readable ID
     * @param {import('./cn_storey').cn_storey} storey
     * @returns {string}
     */
    build_readable_id(storey) {
        if (!this.valid || !this.wall_interface)
            return super.build_readable_id(storey);
        let side = this.wall_interface.spaces.findIndex(sp => !sp.outside);
        if (side < 0) side = 0;
        return this.wall_interface.space_segments[side].build_readable_id(storey) + "B" + (1 + this.wall.openings.indexOf(this));
    }

    /**
     * Returns true if other openings overlaps this (regardless of the 'valid' parameters)
     * @param {import('./cn_opening').cn_opening | cn_niche} other_element
     */
    overlaps(other_element) {
        if (other_element.constructor == cn_niche && other_element.wall_side != this.wall_side)
            return false;
        return this.get_vertical_box(-0.001).intersects(other_element.get_vertical_box(-0.001));
    }

    get_storey_z() {
        return this.compute_offset() + this.z;
    }

    /**
     * Returns the opening vertical offset, relatively to 0.
     * @returns {number}
     */
    compute_offset() {
        if (!this.wall) return 0;
        if (!this.wall_interface) return 0;
        if (this.wall_interface.spaces[this.wall_side].outside)
            return 0;
        return this.wall_interface.spaces[this.wall_side].slab_offset;
    }

    /**
     * Returns the vertices that maque the shape of the niche
     * @returns {number[][]}
     */
    get_vertices() {
        if (this.wall == null) return [];

        const p0 = this.wall.compute_side_position_from_abcissa(this.wall_position, this.wall_side);
        const p1 = this.wall.compute_side_position_from_abcissa(this.wall_position + this.width, this.wall_side);
        let normal = (this.wall_side == 0) ? this.wall.bounds.normal : cn_mul(this.wall.bounds.normal, -1);
        let depth = Math.min(this.depth, this.wall.wall_type.thickness);
        const pp0 = cn_add(p0, cn_mul(normal, depth));
        const pp1 = cn_add(p1, cn_mul(normal, depth));
        return [p0, p1, pp1, pp0];
    }

    update_position() {
        if (!isNaN(this.lock_position) && this.get_candidate_locks().includes(this.wall_lock_vertex))
            this.wall_position = this.wall.compute_abcissa(this.wall_lock_vertex.position) + this.lock_position;
        else
            this.update_wall_position();
    }

    check_wall() {
        if (this.wall == null) return false;

        var x0 = this.wall_position;
        var x1 = x0 + this.width;
        if (x0 < this.wall.opening_area[0] - 0.001) return false;
        if (x1 > this.wall.opening_area[1] + 0.001) return false;

        if (this.wall.openings.find(op => op.valid && this.overlaps(op)))
            return false;

        if (this.wall.niches.find(niche => niche.valid && niche != this && this.overlaps(niche)))
            return false;

        return true;
    }

    /**
     * Puts the opening on a wall
     * @param {import('./cn_wall').cn_wall} wall
     * @param {cn_transaction_manager} transaction_manager
     */
    set_wall(wall, transaction_manager) {
        if (wall == this.wall) return;
        if (transaction_manager) {
            transaction_manager.push_item_set(this, 'wall');
            transaction_manager.push_item_set(this.wall, 'niches');
            transaction_manager.push_item_set(wall, 'niches');
        }
        var index = this.wall.niches.indexOf(this);
        if (index >= 0) this.wall.niches.splice(index, 1);
        this.wall = wall;
        wall.niches.push(this);
        this.parent = wall;
    }

    update_wall_position() {
        let candidate_locks = this.get_candidate_locks();
        if (!candidate_locks.includes(this.wall_lock_vertex))
            this.wall_lock_vertex = candidate_locks[0];
        this.lock_position = this.wall_position - this.wall.compute_abcissa(this.wall_lock_vertex.position);
    }

    /**
     * Returns potential vertex lock for each of 6 possible locks
     * @returns {cn_vertex[]}
     */
    get_candidate_locks() {
        return [this.wall.vertices[0], this.wall.vertices[1]].concat(this.wall.supported_vertices.filter(v => v.support_wall_side < 2 && v.walls.length));
    }

    /**
     * returns vertical bounding box on wall
     * @returns {cn_box}
     */
    get_vertical_box(tolerance = 0) {
        let box = new cn_box();
        let z0 = this.get_storey_z();
        box.enlarge_point([this.wall_position, z0]);
        box.enlarge_point([this.wall_position + this.width, z0 + this.height]);
        if (tolerance) box.enlarge_distance(tolerance);
        return box;
    }
    //***********************************************************************************
    //**** Draw
    //***********************************************************************************
    draw(camera, add_classes = [], fill = '') {
        let vertices = this.get_vertices();
        if (vertices.length == 0) return "";

        let draw_classes = "niche";
        if (add_classes.indexOf('mouseover') >= 0) draw_classes += " mouseover";
        if (add_classes.indexOf('selected') >= 0) draw_classes += " selected";
        if (!this.valid) draw_classes += " not_valid";

        return camera.draw_path(vertices, draw_classes, true);
    }

    draw_id_label(camera, storey) {
        const p0 = this.wall.compute_side_position_from_abcissa(this.wall_position + 0.5 * this.width, this.wall_side);
        return this._draw_id_label(camera, storey, camera.world_to_screen(p0));
    }
    //***********************************************************************************
    /**
     * Draws in svg the measures of the opening (width, height, z)
     * @param {cn_camera} camera
     * @param {string[]} add_classes
     * @param {boolean | {selected:boolean, position:number[]}} opening_measure
     * @param {boolean | Array<{selected:boolean, position:number[]}>} opening_wall_measure
     * @returns {string} html string
     */
    draw_measures(camera, add_classes, opening_measure = true, opening_wall_measure = true) {
        var html = '';
        if (!this.wall || !this.valid) return html;
        if (!opening_wall_measure && !opening_measure) return html;

        let width = this.width;
        var p0_0 = cn_add(this.wall.bounds.pmin, cn_mul(this.wall.bounds.direction, this.wall_position));
        var p0_1 = cn_add(this.wall.bounds.pmin, cn_mul(this.wall.bounds.direction, this.wall_position + width));
        var nn = cn_mul(this.wall.bounds.normal, this.wall.wall_type.thickness);
        var p1_0 = cn_add(p0_0, nn);
        var p1_1 = cn_add(p0_1, nn);

        //*** Draw the measure of the opening itself */
        if (opening_measure) {
            const save_results = typeof (opening_measure) == 'object';
            var p0, p1;
            if (this.wall_interface && this.wall_interface.spaces[1].outside) {
                p0 = p1_1;
                p1 = p1_0;
            } else {
                p0 = p0_0;
                p1 = p0_1;
            }

            var txt = '';
            txt += (width * 100).toFixed(0) + ' x ' + (this.height * 100).toFixed(0) + ' x ' + (this.depth * 100).toFixed(0);
            txt += ' z=' + (this.z * 100).toFixed(0);

            html = camera.draw_measure(p0, p1, (typeof (opening_measure) == 'object') ? opening_measure.position : null, (typeof (opening_measure) == 'object') ? opening_measure.selected : false, 40, add_classes, txt);
        }

        //*** Draw the measure of the opening to what is surrounding */
        if (opening_wall_measure) {
            const save_results = typeof (opening_wall_measure) == 'object';
            for (var sense = 0; sense < 2; sense++) {

                let t_opening = 0;
                let t = this.wall_position + width * sense;

                //*** Maybe an opening? */
                /*const other_opening = (sense > 0) ? this.next_valid_niche() : this.previous_valid_niche();
                if (other_opening)
                    t_opening = (sense > 0) ? other_opening.position : other_opening.position + other_opening.get_width();
                else
                    t_opening = NaN;*/
                t_opening = NaN;

                let side = this.wall_side;
                let t0 = t_opening;
                const space_segment = this.wall.space_segments.filter(ss => ss.wall_side == side).find(ss => t >= ss.wall_positions[side] && t <= ss.wall_positions[1 - side]);
                if (space_segment) {
                    let t_ss = space_segment.wall_positions[(sense == side) ? 0 : 1];
                    if (isNaN(t_opening) || (sense > 0 && t_ss < t_opening) || (sense == 0 && t_ss > t_opening))
                        t0 = t_ss;
                }

                if (!isNaN(t0)) {
                    const p0 = this.wall.compute_side_position_from_abcissa((side == sense) ? t0 : t, side);
                    const p1 = this.wall.compute_side_position_from_abcissa((side == sense) ? t : t0, side);
                    html += camera.draw_measure(p0, p1, (save_results) ? opening_wall_measure[sense].position : null, (save_results) ? opening_wall_measure[sense].selected : false, 40, add_classes);
                }
                else if (save_results)
                    opening_wall_measure[sense].position = [NaN, NaN, NaN];
            }
        }

        return html;
    }

    //***********************************************************************************
    //**** Contains
    //***********************************************************************************
    contains(point, tolerance = 0) {
        let vertices = this.get_vertices();
        if (vertices.length < 4) return false;

        var d = cn_sub(point, vertices[0]);
        let dx = cn_sub(vertices[1], vertices[0]);
        cn_normalize(dx);
        let x = cn_dot(d, dx);
        if (x < -tolerance || x > this.width + tolerance) return false;

        let dy = cn_sub(vertices[3], vertices[0]);
        cn_normalize(dy);
        let y = cn_dot(d, dy);
        if (y < -tolerance || y > this.depth + tolerance) return false;

        return true;
    }

    //***********************************************************************************
    /**
     * Returns bounding box
     * @returns {cn_box}
     */
    get_bounding_box() {
        var box = new cn_box();
        this.get_vertices().forEach(v => box.enlarge_point(v));
        return box;
    }

    //***********************************************************************************
    //**** Contained by box ?
    //***********************************************************************************
    contained_by_box(box) {
        return !this.get_vertices().find(v => !box.contains_point(v));
    }

    /**
     * Accept element visitor
     *
     * @param {cn_element_visitor} element_visitor
     */
    accept_visitor(element_visitor) {
        element_visitor.visit_niche(this);
    }

    build_piercing_polygon() {
        // @ts-ignore
        return this.get_lazy_data(
            () => {
                return this._build_piercing_polygon();
            },
            'piercing_polygon'
        );
    }

    _build_piercing_polygon() {
        if (!this.wall) return null;
        let z0 = this.get_storey_z();
        let p0 = cnx_clone(this.wall.compute_side_position_from_abcissa(this.wall_position, this.wall_side), z0);
        let normal = cnx_clone(this.wall.bounds.normal);
        if (this.wall_side == 1) normal = cnx_mul(normal, -1);
        let pg = new fh_polygon(p0, normal);
        let contour = [p0];
        contour.push(cnx_clone(this.wall.compute_side_position_from_abcissa(this.wall_position + this.width, this.wall_side), z0));
        let shape_height = Math.min(this.shape_height, this.height);
        if (this.element_type.shape == "rectangle") {
            contour.push(cnx_clone(this.wall.compute_side_position_from_abcissa(this.wall_position + this.width, this.wall_side), z0 + this.height));
            contour.push(cnx_clone(this.wall.compute_side_position_from_abcissa(this.wall_position, this.wall_side), z0 + this.height));
        }
        else if (this.element_type.shape == "triangle") {
            contour.push(cnx_clone(this.wall.compute_side_position_from_abcissa(this.wall_position + this.width, this.wall_side), z0 + this.height - shape_height));
            contour.push(cnx_clone(this.wall.compute_side_position_from_abcissa(this.wall_position + 0.5 * this.width, this.wall_side), z0 + this.height));
            contour.push(cnx_clone(this.wall.compute_side_position_from_abcissa(this.wall_position, this.wall_side), z0 + this.height - shape_height));
        }
        else if (this.element_type.shape == "round") {
            for (var theta = 0; theta <= 180; theta += 5) {
                let a = theta * Math.PI / 180;
                let cosa = Math.cos(a) * 0.5 * this.width;
                let sina = Math.sin(a) * shape_height;
                contour.push(cnx_clone(this.wall.compute_side_position_from_abcissa(this.wall_position + 0.5 * this.width + cosa), z0 + this.height - shape_height + sina));
            }
        }
        pg.add_contour(contour);
        return pg;
    }

    build_piercing_solid() {
        // @ts-ignore
        return this.get_lazy_data(
            () => {
                return this._build_piercing_solid();
            },
            'piercing_solid'
        );
    }

    _build_piercing_solid() {
        let pg = this.build_piercing_polygon();
        if (!pg) return null;
        let solid = new fh_solid();
        let normal = cnx_clone(this.wall.bounds.normal);
        if (this.wall_side == 1) normal = cnx_mul(normal, -1);
        let depth = Math.min(this.depth, this.wall.wall_type.thickness);
        solid.extrusion(pg, cnx_mul(normal, depth));
        return solid;
    }

    get_facing_groups() {
        return [new cn_facing_group("Revêtements de niche", "hole", 0, CN_FACING_SUPPORTS.WALL, [
            new cn_constant('Appui', 'lowsill'),
            new cn_constant('Embrasure', 'reveal'),
            new cn_constant('Linteau', 'lintel'),
            new cn_constant('Fond', 'back')
        ])];
    }

    compute_facing_count() {
        [0, 1].forEach(side => {
            for (var key in this.facings[side]) {
                if (this.facings[side][key])
                    this.facings[side][key].count++;
            }
        });
    }

    
    /**
     * TODO : derivate is element can be aligned
     * @returns {boolean}
     */
    is_alignable() {
        return this.valid;
    }

    /**
     * TODO : derivate this to manage alignment
     * Returns current align center
     * @returns {number[]}
     */
    get_align_center() {
        return this.wall.compute_side_position_from_abcissa(this.position + 0.5 * this.width,this.wall_side);
    }

    /**
     * TODO : derivate this to manage alignment
     * Returns true if center was successfully set
     * @param {number[]} p
     * @param {number[]} center
     * @param {import('../utils/cn_transaction_manager').cn_transaction_manager} transaction_manager
     * @returns {boolean}
     */
    set_align_center(p, center, transaction_manager = null) {
        let w = this.wall.parent.find_wall(p,0.01);
        if (w) {
            this.set_wall(w,transaction_manager);
            if (transaction_manager) transaction_manager.push_item_set(this,["wall_side","position"]);
            this.wall_side = w.compute_side(p);
            this.position = this.wall.compute_abcissa(p) - 0.5 * this.width;
        }
        else {
            let dir = cn_sub(p,center);
            if (cn_normalize(dir) < 0.001) return false;
            let impact0 = this.wall.parent.raytrace_walls(p,dir,1000);
            let impact1 = this.wall.parent.raytrace_walls(p,cn_mul(dir,-1),1000);
            let impact = (impact0 && impact1)?impact0.distance < impact1.distance?impact0:impact1:impact0 || impact1;
            if (!impact) return false;
            
            this.set_wall(impact.wall,transaction_manager);
            if (transaction_manager) transaction_manager.push_item_set(this,["wall_side","position"]);
            this.wall_side = impact.side;
            this.position = impact.abcissa - 0.5 * this.width;
        }

        this.update_wall_position();
        if (transaction_manager)
            this.wall.parent.update_space_numerotation();
        return true;
    }
}

